import { useIntl } from 'react-intl';
import { useEffect, useRef, useState } from 'react';
import { CustomTable, CustomTableRef } from 'medere-ui-framework';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { AccountDTO } from '../../dto/CurrentAccountDTO';
import AccountMovementsView from './AccountMovementsView';
import { useCurrentAccountService } from '../../servicesHooks/CurrentAccount/useCurrentAccountService';

function formatMoney(amount: number | null): string {
    if (amount === null || amount === undefined) return '—';
    return `$ ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('es-AR');
}

function BalanceBadge({ balance }: { balance: number | null }) {
    if (balance === null || balance === undefined) {
        return <span style={{ color: '#9CA3AF' }}>Sin movimientos</span>;
    }
    const isPositive = balance >= 0;
    return (
        <span style={{
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            background: isPositive ? '#D1FAE5' : '#FEE2E2',
            color: isPositive ? '#065F46' : '#991B1B',
            whiteSpace: 'nowrap',
        }}>
            {formatMoney(balance)}
        </span>
    );
}

export default function CurrentAccountView() {
    const intl = useIntl();
    const { getAccounts } = useCurrentAccountService();
    const tableRef = useRef<CustomTableRef>(null);
    const [selectedAccount, setSelectedAccount] = useState<AccountDTO | null>(null);

    const [fromEntityName, setFromEntityName] = useState('');
    const [toEntityName, setToEntityName] = useState('');
    const [balanceType, setBalanceType] = useState<string | null>(null);
    const [params, setParams] = useState<{ fromEntityName?: string; toEntityName?: string; balanceType?: string }>({});

    const balanceOptions: { label: string; value: string | null }[] = [
        { label: 'Todos', value: null },
        { label: 'Acreedor', value: 'acreedor' },
        { label: 'Deudor', value: 'deudor' },
    ];

    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setParams({
                fromEntityName: fromEntityName.trim() || undefined,
                toEntityName: toEntityName.trim() || undefined,
                balanceType: balanceType ?? undefined,
            });
            tableRef.current?.refreshTable?.();
        }, 400);
        return () => clearTimeout(timer);
    }, [fromEntityName, toEntityName, balanceType]);

    if (selectedAccount) {
        return (
            <AccountMovementsView
                account={selectedAccount}
                onBack={() => setSelectedAccount(null)}
            />
        );
    }

    const fromFilterElement = (
        <InputText
            value={fromEntityName}
            onChange={e => setFromEntityName(e.target.value)}
            placeholder={intl.formatMessage({ id: 'FilterFromEntity', defaultMessage: 'Buscar origen...' })}
            className="p-column-filter"
            style={{ width: '100%' }}
        />
    );

    const toFilterElement = (
        <InputText
            value={toEntityName}
            onChange={e => setToEntityName(e.target.value)}
            placeholder={intl.formatMessage({ id: 'FilterToEntity', defaultMessage: 'Buscar destino...' })}
            className="p-column-filter"
            style={{ width: '100%' }}
        />
    );

    return (
        <div className="px-4 flex flex-column viewContainer">
            <div className="flex align-items-center gap-4 mb-4" style={{ flexWrap: 'wrap' }}>
                <h2 className="text-2xl mainTitle" style={{ flex: 1, minWidth: 200 }}>
                    {intl.formatMessage({ id: 'CurrentAccounts', defaultMessage: 'Cuentas Corrientes' })}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#5A6676', fontWeight: 500 }}>Saldo:</span>
                    <div style={{ display: 'inline-flex', border: '1.5px solid #01717C', borderRadius: 6, overflow: 'hidden' }}>
                        {balanceOptions.map((opt, idx) => (
                            <button
                                key={opt.value ?? 'all'}
                                onClick={() => setBalanceType(opt.value)}
                                style={{
                                    padding: '5px 14px',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    fontFamily: 'Inter, sans-serif',
                                    border: 'none',
                                    borderRight: idx < balanceOptions.length - 1 ? '1px solid #01717C' : 'none',
                                    cursor: 'pointer',
                                    background: balanceType === opt.value ? '#01717C' : 'transparent',
                                    color: balanceType === opt.value ? '#fff' : '#01717C',
                                    transition: 'background 0.15s, color 0.15s',
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <CustomTable
                idTable="tableCurrentAccounts"
                ref={tableRef}
                getItems={getAccounts}
                className="crudTable"
                filterRow
                hideFiltersButton
                params={params}
            >
                <Column
                    field="account"
                    header={intl.formatMessage({ id: 'AccountNumber', defaultMessage: 'N° Cuenta' })}
                    className="text-center"
                    headerClassName="col-center"
                    style={{ width: '100px' }}
                />
                <Column
                    field="fromentityname"
                    header={intl.formatMessage({ id: 'FromEntity', defaultMessage: 'Entidad Origen' })}
                    filter
                    filterElement={fromFilterElement}
                />
                <Column
                    field="toentityname"
                    header={intl.formatMessage({ id: 'ToEntity', defaultMessage: 'Entidad Destino' })}
                    filter
                    filterElement={toFilterElement}
                />
                <Column
                    field="movementcount"
                    header={intl.formatMessage({ id: 'Movements', defaultMessage: 'Movimientos' })}
                    className="text-center"
                    headerClassName="col-center"
                    style={{ width: '120px' }}
                />
                <Column
                    field="lastmovementdate"
                    header={intl.formatMessage({ id: 'LastMovement', defaultMessage: 'Último Movimiento' })}
                    className="text-center"
                    headerClassName="col-center"
                    body={(row: AccountDTO) => formatDate(row.lastmovementdate)}
                    style={{ width: '160px' }}
                />
                <Column
                    field="currentbalance"
                    header={intl.formatMessage({ id: 'CurrentBalance', defaultMessage: 'Saldo Actual' })}
                    className="text-right"
                    headerClassName="col-right"
                    body={(row: AccountDTO) => <BalanceBadge balance={row.currentbalance} />}
                    style={{ width: '180px' }}
                />
                <Column
                    body={(row: AccountDTO) => (
                        <Button
                            icon="pi pi-list"
                            className="p-button-rounded p-button-outlined tableButton"
                            tooltip={intl.formatMessage({ id: 'SeeMovements', defaultMessage: 'Ver Movimientos' })}
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => setSelectedAccount(row)}
                        />
                    )}
                    style={{ textAlign: 'center', width: '5rem' }}
                    headerStyle={{ textAlign: 'center', width: '5rem' }}
                />
            </CustomTable>
        </div>
    );
}
